import React, { useState } from 'react'
import shortid from 'shortid'

import Button from '@react/react-spectrum/Button'
import Dialog from '@react/react-spectrum/Dialog'
import FieldLabel from '@react/react-spectrum/FieldLabel'
import ModalTrigger from '@react/react-spectrum/ModalTrigger'
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger'
import Tooltip from '@react/react-spectrum/Tooltip'
import { Table, THead, TBody, TH, TR, TD } from '@react/react-spectrum/Table'
import Textfield from '@react/react-spectrum/Textfield'
import Select from '@react/react-spectrum/Select'
import ComboBox from '@react/react-spectrum/ComboBox'
import ColorInput from 'components/ColorInput'

import Delete from '@react/react-spectrum/Icon/Delete'
import Edit from '@react/react-spectrum/Icon/Edit'
import Info from '@react/react-spectrum/Icon/Info'
import ArrowUp from '@react/react-spectrum/Icon/ArrowUp'
import ArrowDown from '@react/react-spectrum/Icon/ArrowDown'

const RENDER_AS_OPTIONS = [
  { label: 'Ribbon', value: 'ribbon' },
  { label: 'Banner', value: 'banner' },
  { label: 'Card', value: 'card' },
]

const CARD_VARIANT_OPTIONS = [
  { label: 'Image on the left', value: 'horizontalLeft' },
  { label: 'Image on the right', value: 'imageRight' },
]

const ALIGNMENT_OPTIONS = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
]

const VERTICAL_ALIGNMENT_OPTIONS = [
  { label: 'Top', value: 'top' },
  { label: 'Middle', value: 'middle' },
  { label: 'Bottom', value: 'bottom' },
]

const normalizeRows = (surfaces) =>
  (surfaces || []).map((surface) => {
    const id = surface.id || shortid()
    return { ...surface, id, key: surface.key || id }
  })

const createRow = () => {
  const id = shortid()
  return { id, key: id, renderAs: 'ribbon', width: '900px', height: '300px' }
}

export function SurfacesEditor({ surfaces, disabled, onChange, surfaceOptions }) {
  const [rows, setRows] = useState(() => normalizeRows(surfaces))
  const hasRows = rows.length > 0

  const commit = (newRows) => {
    setRows(newRows)
    onChange(newRows)
  }

  const updateRow = (id, patch) => commit(rows.map((row) => (row.id === id ? { ...row, ...patch } : row)))

  const addRow = () => commit([...rows, createRow()])

  const deleteRow = (id) => commit(rows.filter((row) => row.id !== id))

  const moveRow = (id, direction) => {
    const index = rows.findIndex((row) => row.id === id)
    const newIndex = index + direction
    if (newIndex < 0 || newIndex >= rows.length) return

    const updated = [...rows]
    const [moved] = updated.splice(index, 1)
    updated.splice(newIndex, 0, moved)
    commit(updated)
  }

  return (
    <div className='SurfacesEditor'>
      {hasRows && (
        <Table className='SurfacesEditor__table'>
          <THead>
            <TH width='220'>Surface</TH>
            <TH width='200'>CSS Selector</TH>
            <TH width='120'>Render as</TH>
            <TH>Options</TH>
            <TH width='110'>Actions</TH>
          </THead>
          <TBody>
            {rows.map((row, index) => (
              <SurfaceRow
                key={row.id}
                row={row}
                disabled={disabled}
                isFirst={index === 0}
                isLast={index === rows.length - 1}
                surfaceOptions={surfaceOptions}
                onChange={(patch) => updateRow(row.id, patch)}
                onMoveUp={() => moveRow(row.id, -1)}
                onMoveDown={() => moveRow(row.id, 1)}
                onDelete={() => deleteRow(row.id)}
              />
            ))}
          </TBody>
        </Table>
      )}
      <div className='SurfacesEditor__addRow'>
        <Button label='Add Surface' variant='primary' onClick={addRow} disabled={disabled} />
      </div>
    </div>
  )
}

function SurfaceRow({ row, disabled, isFirst, isLast, surfaceOptions, onChange, onMoveUp, onMoveDown, onDelete }) {
  return (
    <TR>
      <TD>
        <ComboBox
          value={row.surface || ''}
          options={(surfaceOptions || []).map((option) => option.surface)}
          placeholder='Select or enter a surface'
          onChange={(surface) => onChange({ surface })}
          onSelect={(surface) => onChange({ surface })}
          disabled={disabled}
        />
      </TD>
      <TD>
        <Textfield
          value={row.selector || ''}
          placeholder='.hero-banner'
          onChange={(selector) => onChange({ selector })}
          disabled={disabled}
        />
      </TD>
      <TD>
        <Select
          value={row.renderAs || 'ribbon'}
          options={RENDER_AS_OPTIONS}
          onChange={(renderAs) => onChange({ renderAs })}
          disabled={disabled}
        />
      </TD>
      <TD>
        <RenderOptionsButton row={row} onChange={onChange} disabled={disabled} />
      </TD>
      <TD>
        <RowActions
          disabled={disabled}
          disableMoveUp={isFirst}
          disableMoveDown={isLast}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          onDelete={onDelete}
        />
      </TD>
    </TR>
  )
}

function RenderOptionsButton({ row, disabled, onChange }) {
  return (
    <ModalTrigger>
      <Button variant='secondary' icon={<Edit size='XS' />} disabled={disabled}>
        Options
      </Button>
      <RenderOptionsDialog row={row} onChange={onChange} disabled={disabled} />
    </ModalTrigger>
  )
}

function RenderOptionsDialog({ row, disabled, onChange, ...otherProps }) {
  const [draft, setDraft] = useState(row)

  const updateDraft = (patch) => setDraft((d) => ({ ...d, ...patch }))

  return (
    <Dialog
      {...otherProps}
      title='Render options'
      confirmLabel='Save'
      cancelLabel='Cancel'
      onConfirm={() => onChange(draft)}
      role='dialog'
    >
      <RenderOptions row={draft} onChange={updateDraft} disabled={disabled} />
    </Dialog>
  )
}

function RenderOptions({ row, disabled, onChange }) {
  const renderAs = row.renderAs || 'ribbon'
  const isBannerOrCard = renderAs === 'banner' || renderAs === 'card'

  return (
    <div className='SurfacesEditor__options'>
      {renderAs === 'card' && (
        <FieldLabel label='Image layout'>
          <Select
            value={row.variant || 'horizontalLeft'}
            options={CARD_VARIANT_OPTIONS}
            onChange={(variant) => onChange({ variant })}
            disabled={disabled}
          />
        </FieldLabel>
      )}
      {isBannerOrCard && (
        <FieldLabel label='Horizontal alignment'>
          <Select
            value={row.alignment || 'center'}
            options={ALIGNMENT_OPTIONS}
            onChange={(alignment) => onChange({ alignment })}
            disabled={disabled}
          />
        </FieldLabel>
      )}
      {isBannerOrCard && (
        <FieldLabel label='Vertical alignment'>
          <Select
            value={row.verticalAlignment || 'middle'}
            options={VERTICAL_ALIGNMENT_OPTIONS}
            onChange={(verticalAlignment) => onChange({ verticalAlignment })}
            disabled={disabled}
          />
        </FieldLabel>
      )}
      {renderAs === 'banner' && (
        <FieldLabel label='Width' className='SurfacesEditor__heightField'>
          <Textfield
            value={row.width || '900px'}
            placeholder='auto or 900px'
            onChange={(width) => onChange({ width })}
            disabled={disabled}
          />
        </FieldLabel>
      )}
      {renderAs === 'banner' && (
        <FieldLabel label='Height' className='SurfacesEditor__heightField'>
          <Textfield
            value={row.height || '300px'}
            placeholder='auto or 300px'
            onChange={(height) => onChange({ height })}
            disabled={disabled}
          />
        </FieldLabel>
      )}
      <FieldLabel label='Background colour'>
        <ColorInput
          placeholder='#ffffff'
          value={row.backgroundColor || ''}
          onChange={(backgroundColor) => onChange({ backgroundColor })}
          disabled={disabled}
        />
      </FieldLabel>
      <FieldLabel label='Text colour'>
        <ColorInput
          placeholder='#f9e322'
          value={row.color || ''}
          onChange={(color) => onChange({ color })}
          disabled={disabled}
        />
      </FieldLabel>
      <FieldLabel
        label={
          <>
            Custom CSS
            <OverlayTrigger placement='right'>
              <Button variant='tool' icon={<Info size='XS' />} />
              <Tooltip variant='info'>
                Target the class the render style uses: <code>.ribbon</code> for Ribbon,{' '}
                <code>.banner</code> for Banner, or <code>.card</code> for Card. These are fixed
                class names built into the renderer, not something you name yourself.
              </Tooltip>
            </OverlayTrigger>
          </>
        }
      >
        <Textfield
          multiLine
          rows={4}
          value={row.customCss || ''}
          placeholder={'.ribbon { justify-content: flex-start; }'}
          onChange={(customCss) => onChange({ customCss })}
          disabled={disabled}
        />
      </FieldLabel>
    </div>
  )
}

function RowActions({ disabled, disableMoveUp, disableMoveDown, onMoveUp, onMoveDown, onDelete }) {
  return (
    <div className='SurfacesEditor__rowActions'>
      <Button variant='tool' aria-label='Move Up' icon={<ArrowUp />} onClick={onMoveUp} disabled={disabled || disableMoveUp} />
      <Button variant='tool' aria-label='Move Down' icon={<ArrowDown />} onClick={onMoveDown} disabled={disabled || disableMoveDown} />
      <Button variant='tool' aria-label='Delete' icon={<Delete />} onClick={onDelete} disabled={disabled} />
    </div>
  )
}
